import { motion } from 'framer-motion';
import { Box, Column, Radio, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { isPendingBankPayment, useCheckPaymentExpiration } from '~/domain/Payments';

const opacityVariants = {
  checked: { opacity: 1 },
  unchecked: { opacity: 0 },
};

// DS Radio sm indicator (16px) + gap-2 (8px) = offset for content below the radio row
const EXPIRY_INDENT = 24;

export const PaymentLine = ({
  isSelected,
  item,
  handleRadioButtonClick,
  handleMakeDefault,
  handleOnDelete,
  showActions = true,
  showExpiration = false,
  renderAction,
  // The location whose payment method this is; the selected office when omitted.
  officeId,
}) => {
  const { checkPaymentExpiration } = useCheckPaymentExpiration(officeId);
  const { isExpired, isExpiring } = checkPaymentExpiration(item);
  const isPending = isPendingBankPayment(item);

  const getWarningColor = () => {
    if (isExpired) return colors.error[500];
    if (isExpiring) return colors.warning[500];
    return colors.neutral[400];
  };

  return (
    <Column gap={0} data-cy="payment-line" className="global_font">
      <Row gap={2} align="center">
        <Radio
          data-cy="payment-line-radio-button"
          value={item.id}
          checked={!isPending && isSelected}
          disabled={isPending}
          onChange={() => {
            if (!isPending) {
              handleRadioButtonClick(item);
            }
          }}
          size="sm"
          label={
            <Row gap={2} align="center">
              <Typography
                data-cy="payment-line-info"
                variant="body1"
                color={colors.neutral[600]}
              >
                {item.brand || item.bank_name || 'Bank Account'} - {item.last4}
              </Typography>

              {item.is_selected && !isPending && (
                <Box
                  style={{
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: colors.secondary[500],
                    borderRadius: 4,
                  }}
                  px={2}
                >
                  <Typography size="caption_sm" bold color={colors.shades[0]}>
                    DEFAULT
                  </Typography>
                </Box>
              )}

              {showActions && isSelected && !isPending && (
                <motion.div
                  animate="checked"
                  initial="unchecked"
                  variants={opacityVariants}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  {!item.is_selected && !item.isExpired && (
                    <button
                      data-cy="btn-payment-line-make-default"
                      type="button"
                      style={{ all: 'unset', cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); handleMakeDefault(item.id); }}
                    >
                      <Typography variant="body1" bold color={colors.secondary[500]}>
                        Make Default
                      </Typography>
                    </button>
                  )}
                  <Typography variant="body1" color={colors.neutral[600]}>
                    |
                  </Typography>
                  <button
                    data-cy="btn-payment-line-delete"
                    type="button"
                    style={{ all: 'unset', cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); handleOnDelete(item); }}
                  >
                    <Typography variant="body1" bold color={colors.secondary[500]}>
                      Delete
                    </Typography>
                  </button>
                </motion.div>
              )}
            </Row>
          }
        />

        {/* Rendered outside the Radio's label so a pending row's disabled
            (greyed/non-interactive) styling never applies to the badge or action. */}
        {isPending && (
          <Box
            data-cy="payment-line-pending-badge"
            style={{
              height: 20,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: colors.warning[50],
              borderRadius: 4,
            }}
            px={2}
          >
            <Typography size="caption_sm" bold color={colors.warning[600]}>
              PENDING
            </Typography>
          </Box>
        )}

        {renderAction?.(item)}
      </Row>

      {showExpiration && (
        <Typography
          size="body_small"
          color={getWarningColor()}
          style={{ paddingLeft: EXPIRY_INDENT, textAlign: 'left' }}
        >
          Expires: {item.expirationDate.format('MM/YY')}
        </Typography>
      )}
    </Column>
  );
};
